/* eslint-disable max-lines -- one row renders nine independent state dimensions (AP-764) plus the inline-edit lifecycle */
import {
  useCallback,
  useState,
  useRef,
  useEffect,
  type KeyboardEvent,
} from 'react';
import { Tooltip } from 'antd';
import type { RangeDirection } from '@/shared/utils/formatNormalRange';
import type { RangeSource } from '@/shared/utils/rangeSource';
import type { RowMarker } from '../rowModel';
import { PencilIcon } from './PencilIcon';
import { CalculatedIndicator } from './CalculatedIndicator';
import { RangePopover } from './RangePopover';
import { navigateWorksheetTab } from '../../shared/utils/tabNavigation';
import styles from './MeasurementRow.module.css';

// Concordance glyph + accessible label; `agree` renders nothing. The disagree
// tooltip is built per-row (it carries the SR value the AI disagrees with).
const CONCORDANCE_MARK: Partial<
  Record<
    'disagree' | 'indeterminable',
    { glyph: string; className: string; label: string }
  >
> = {
  disagree: {
    glyph: '!',
    className: styles.concordanceDisagree,
    label: 'AI and SR disagree',
  },
  indeterminable: {
    glyph: '?',
    className: styles.concordanceIndeterminable,
    label: 'No SR to compare',
  },
};

// Passive glanceable glyphs get a slightly lazier tooltip than antd's default
// so a cursor sweeping across a dense panel doesn't flash hover chatter.
const GLYPH_TOOLTIP_DELAY = 0.3;

interface MeasurementRowProps {
  label: string;
  value: number | null;
  unit: string | null;
  precision?: number | null;
  isClickable?: boolean;
  /** A drawing tool is armed for this row in the viewer (AP-764 §3.8) — row
   *  highlight + accent label. Same signal as the active measurement. */
  isActive?: boolean;
  /** Left-gutter affordance pencil (AP-764 §3.3): filled = an annotation is
   *  drawn (click navigates to it), outline = a drawing tool exists but nothing
   *  is drawn yet (click to measure), unavailable = the tool can't be used on
   *  the selected image, none = no tool. */
  pencil?: 'filled' | 'outline' | 'unavailable' | 'none';
  /** Hover text for the unavailable pencil — which image the tool needs. */
  unavailableHint?: string;
  /** Source marker + AI-vs-SR concordance for the displayed value (AP-764
   *  §3.1/§3.2). The discriminated union enforces invariant I1: concordance
   *  exists only on an AI-sourced value. null = SR / no value (no marker). */
  marker?: RowMarker;
  /** Range state (AP-764 §3.4): indicator right of the unit — ↑/↓ outside
   *  the band (+ warning tint), nothing for normal/unmeasured (band shows on
   *  unit hover), and a red dot when the row has NO reference range (null). */
  range?: RangeDirection | 'normal' | 'unmeasured' | null;
  /** Formatted normal band ("4.2 - 5.8", "< 5.9") for the range tooltip;
   *  null/absent = tooltip states the direction only. */
  normalRange?: string | null;
  /** Caveat under the band when it is an age fallback (rowModel.rangeNote). */
  rangeNote?: string | null;
  /** Provenance of the band: a short guideline label, plus an "Open guideline"
   *  action when the PDF is hosted. Rendered under the band in the range tooltip. */
  source?: RangeSource | null;
  /** Opens the hosted guideline PDF (`source.file`) at `source.page`; without
   *  it the label renders alone. */
  onOpenSource?: (file: string, page?: number | null) => void;
  /** This row's annotation is the selected trace in the viewer (AP-764 §3.10) —
   *  renders the label bold. Set by clicking the annotation, or the annotated
   *  row's label. Independent of armed / focused. */
  selected?: boolean;
  /** Present ⇒ backend-derived (auto-calculated) row. Shows a passive `fx`
   *  indicator with the formula on hover; editing is not gated. The string is
   *  the raw formula ('' when the row has none). */
  derivedFormula?: string | null;
  /** Operand keys the formula needs but could not resolve (AP-731 R9). When
   *  non-empty the row keeps its value and names these in the hover text. */
  missingInputs?: string[];
  /** The engine auto-updated this row and the change is not yet saved (R7).
   *  Shows the unsaved-change fill; cleared when the worksheet saves
   *  (the caller derives this from the server baseline). */
  unsaved?: boolean;
  /** AP-731 §3 — the row holds a value that is not what its formula
   *  produces. Renders `≠` right of `fx`. */
  diverged?: boolean;
  /** The formula's current result, shown in the converge affordance (§4).
   *  null ⇒ unevaluable, so there is nothing to reset to. */
  calculatedValue?: number | null;
  /** Reset a diverged row to its calculated value (R17). Absent ⇒ not offered. */
  onConverge?: () => void;
  onChange?: (value: number | null) => void;
  onClick?: () => void;
  /** The value input gained DOM focus (editable rows only). The caller clears
   *  the other row emphases here — focusing disarms and deselects (I7). */
  onValueFocus?: () => void;
  disabled?: boolean;
}

export default function MeasurementRow({
  label,
  value,
  unit,
  precision = 0,
  isClickable = false,
  isActive = false,
  pencil = 'none',
  unavailableHint,
  marker = null,
  range = null,
  normalRange = null,
  rangeNote = null,
  source = null,
  onOpenSource,
  selected = false,
  derivedFormula,
  missingInputs,
  unsaved = false,
  diverged = false,
  calculatedValue = null,
  onConverge,
  onChange,
  onClick,
  onValueFocus,
  disabled = false,
}: MeasurementRowProps) {
  const [isEditing, setIsEditing] = useState(false);
  // null until the reader types, so a focused row still shows recalculated values.
  const [editValue, setEditValue] = useState<string | null>(null);
  // Value-input DOM focus (click or Tab/Shift-Tab). DOM focus is inherently
  // singular, so at most one row is ever focused.
  const [inputFocused, setInputFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const formatValue = useCallback(
    (val: number | null): string => {
      if (val === null || val === undefined) return '';
      if (precision !== null && precision !== undefined) {
        return val.toFixed(precision);
      }
      return String(val);
    },
    [precision]
  );

  const handleClick = useCallback(() => {
    if (disabled || !onChange) return;
    setEditValue(null);
    setIsEditing(true);
  }, [disabled, onChange]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // The value the row last committed (or was given): each commit compares
  // against it and resets it, so Enter followed by blur emits only what
  // changed in between (AP-773 §3.1 A5 — type, Enter, keep typing, blur = two
  // commits; Enter then blur with no typing = one).
  const committedRef = useRef(value);
  useEffect(() => {
    committedRef.current = value;
  }, [value]);

  const commitEdit = useCallback(() => {
    // Untouched rows never commit — the value shown may have been recalculated.
    if (!onChange || editValue === null) return;
    if (editValue.trim() === '') {
      // Only clear if it represents an actual change — avoids emitting a
      // null for phantom focus+blur on rows that were already empty.
      if (committedRef.current !== null) {
        committedRef.current = null;
        onChange(null);
      }
      return;
    }
    const numValue = parseFloat(editValue);
    // Same guard as the empty case: a focus+blur without an actual change
    // must not emit — it would stamp the row as a MANUAL override (AP-613).
    if (!Number.isNaN(numValue) && numValue !== committedRef.current) {
      committedRef.current = numValue;
      onChange(numValue);
    }
  }, [editValue, onChange]);

  const handleBlur = useCallback(() => {
    setIsEditing(false);
    commitEdit();
  }, [commitEdit]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Tab') {
        e.preventDefault();
        navigateWorksheetTab(inputRef.current!, e.shiftKey);
        return;
      }
      if (e.key === 'Escape') {
        // Esc defocuses the row (I7); the blur commits like any other blur.
        inputRef.current?.blur();
        return;
      }
      if (!isEditing) return;
      if (e.key === 'Enter') {
        // Commit but stay editable: the reader can keep typing and the
        // next blur / Enter commits only what changed since (AC25).
        commitEdit();
      }
    },
    [isEditing, commitEdit]
  );

  const isEmpty = value === null || value === undefined || value === 0;

  // Derived/indexed values are read-only (R6, locked decision #2) — the engine
  // owns them. Their underlying inputs are separate, non-derived rows and stay
  // editable (they keep their own onChange).
  const isDerived = derivedFormula != null;
  // AP-731 R10 — computed values are editable like any other; editing one
  // diverges it (R5). Pencil and arming stay frozen.
  const isValueEditable = !!onChange && !disabled;
  const isLabelClickable = isClickable && !!onClick;
  const isAbnormal = range === 'high' || range === 'low';

  // Focused underline (AP-764 §3.9): the value input holds DOM focus. Separate
  // from armed (§3.8) — arming does not focus, and focusing an input disarms via
  // the viewer's outside-click handler — so a row is never both. Editable rows
  // also enter edit mode on focus.
  const handleFocus = useCallback(() => {
    setInputFocused(true);
    if (isValueEditable) {
      // Focusing clears the other emphases (I7) — covers Tab-focus too, which
      // never passes through the outside-click disarm path.
      onValueFocus?.();
      if (!isEditing) handleClick();
    }
  }, [isValueEditable, isEditing, handleClick, onValueFocus]);

  const handleFocusOut = useCallback(() => {
    setInputFocused(false);
    if (isEditing) handleBlur();
  }, [isEditing, handleBlur]);

  // Value cell chrome: read-only static text vs the dashed editable field. The
  // focus underline (`valueFocused`) layers a solid accent border on top.
  let valueStateClass: string;
  if (!isValueEditable) valueStateClass = styles.valueDisabled;
  else valueStateClass = styles.valueDisplay;

  // Source marker (AP-764 §3.1/§3.2): a concordance glyph (only on AI) left of
  // the AI sparkle, or the manual circle. null renders nothing. Sits at the
  // right edge of the label cell so markers align down the column.
  const concordanceMark =
    marker?.kind === 'ai' && marker.concordance !== 'agree'
      ? CONCORDANCE_MARK[marker.concordance]
      : undefined;
  // Disagree carries the SR reading it was judged against (AP-776) — the "!"
  // is the whole conflict UI now, so its tooltip must say what SR reported.
  let concordanceTooltip: string | undefined;
  if (marker?.kind === 'ai' && marker.concordance === 'disagree') {
    concordanceTooltip =
      marker.srValue !== null
        ? `Conflicts with SR value: ${formatValue(marker.srValue)}${unit ? ` ${unit}` : ''}`
        : 'Conflicts with value from SR';
  } else if (marker?.kind === 'ai' && marker.concordance === 'indeterminable') {
    concordanceTooltip = 'No SR value to compare against';
  }
  // `sr` is an explicit source that renders nothing (§3.1) — same as no value.
  const markerIndicator = marker !== null && marker.kind !== 'sr' && (
    <span className={styles.markerCell}>
      {concordanceMark && (
        <Tooltip
          title={concordanceTooltip}
          mouseEnterDelay={GLYPH_TOOLTIP_DELAY}
        >
          <span
            className={`${styles.concordance} ${concordanceMark.className}`}
            role="img"
            aria-label={concordanceMark.label}
          >
            {concordanceMark.glyph}
          </span>
        </Tooltip>
      )}
      {marker.kind === 'ai' && (
        <Tooltip title="AI measurement" mouseEnterDelay={GLYPH_TOOLTIP_DELAY}>
          <span
            className={`${styles.mark} ${styles.markAi}`}
            role="img"
            aria-label="AI"
          >
            ✦
          </span>
        </Tooltip>
      )}
      {marker.kind === 'manual' && (
        <Tooltip
          title="Manual measurement"
          mouseEnterDelay={GLYPH_TOOLTIP_DELAY}
        >
          <span
            className={`${styles.mark} ${styles.markManual}`}
            role="img"
            aria-label="Manual override"
          >
            ●
          </span>
        </Tooltip>
      )}
    </span>
  );

  // Left-gutter affordance pencil (fixed width, always reserved so labels
  // align), state resolved by the caller from the row model.
  const pencilIndicator = (
    <span className={styles.pencilGutter} aria-hidden={pencil === 'none'}>
      {pencil === 'filled' && (
        <Tooltip title="Click to view annotation">
          <span
            className={`${styles.pencil} ${styles.pencilFilled}`}
            role="img"
            aria-label="Annotation on image"
          >
            <PencilIcon filled />
          </span>
        </Tooltip>
      )}
      {pencil === 'outline' && (
        <Tooltip title="Click to measure in viewer">
          <span
            className={`${styles.pencil} ${styles.pencilOutline}`}
            role="img"
            aria-label="Measurable, not yet drawn"
          >
            <PencilIcon />
          </span>
        </Tooltip>
      )}
      {pencil === 'unavailable' && (
        <Tooltip title={unavailableHint}>
          <span
            className={`${styles.pencil} ${styles.pencilUnavailable}`}
            role="img"
            aria-label="Not measurable on the selected image"
          >
            <PencilIcon />
          </span>
        </Tooltip>
      )}
    </span>
  );

  const calculatedIndicator = isDerived && (
    <CalculatedIndicator
      formula={derivedFormula ?? null}
      diverged={diverged}
      calculatedText={
        calculatedValue === null ? null : formatValue(calculatedValue)
      }
      enteredText={formatValue(value)}
      missingInputs={missingInputs}
      onConverge={onConverge}
      disabled={disabled}
    />
  );

  const labelInner = (
    <>
      {pencilIndicator}
      <span className={styles.labelText}>{label}</span>
      {calculatedIndicator}
      {markerIndicator}
    </>
  );

  return (
    <div
      className={`${styles.row} ${isActive ? styles.rowArmed : ''} ${unsaved ? styles.rowUnsaved : ''}`}
      data-unsaved={unsaved ? 'true' : undefined}
      data-armed={isActive ? 'true' : undefined}
    >
      {/* Column 1: label (+ gutter pencil, fx, source marker) */}
      {isLabelClickable ? (
        <button
          type="button"
          tabIndex={-1}
          data-measurement-label
          className={`${styles.label} ${styles.labelClickable} ${isActive ? styles.labelActive : ''} ${selected ? styles.labelSelected : ''}`}
          // Arming must never park focus on the label (§3.8) — focus is granted
          // on mousedown, so prevent it; otherwise a later keypress (e.g. the
          // Escape disarm) promotes the silently-focused button to
          // :focus-visible and paints the UA focus ring. Preventing mousedown
          // also suppresses the natural blur of a previously-focused value
          // input, so blur it explicitly to keep the §3.9 contract (focusing
          // and arming are mutually exclusive).
          onMouseDown={(e) => {
            e.preventDefault();
            (document.activeElement as HTMLElement | null)?.blur?.();
          }}
          onClick={onClick}
        >
          {labelInner}
        </button>
      ) : (
        <span
          className={`${styles.label} ${isActive ? styles.labelActive : ''} ${selected ? styles.labelSelected : ''}`}
        >
          {labelInner}
        </span>
      )}

      {/* Column 2: value + unsaved bar (a reserved slot to the RIGHT of the
          input; amber when unsaved, empty otherwise). Focus tracks the accent
          underline; editable rows also enter edit mode on focus/click. */}
      <span className={styles.valueCell}>
        <input
          ref={inputRef}
          type="text"
          inputMode="decimal"
          className={`${styles.value} ${valueStateClass} ${isEmpty && !isEditing ? styles.valueEmpty : ''} ${isAbnormal ? styles.abnormal : ''} ${inputFocused && isValueEditable ? styles.valueFocused : ''}`}
          value={
            isEditing
              ? (editValue ?? (value != null ? String(value) : ''))
              : formatValue(value)
          }
          data-committed={formatValue(value)}
          placeholder="-"
          readOnly={!isEditing}
          tabIndex={isValueEditable ? undefined : -1}
          onFocus={handleFocus}
          onClick={isValueEditable && !isEditing ? handleClick : undefined}
          onChange={isEditing ? (e) => setEditValue(e.target.value) : undefined}
          onBlur={handleFocusOut}
          onKeyDown={isValueEditable ? handleKeyDown : undefined}
        />
        <span className={styles.unsavedBar} aria-hidden="true" />
      </span>

      {/* Column 3: unit + range indicator, under ONE tooltip: the whole cell
          (unit text, arrow or red dot) answers the same question — "what's the
          range situation here?" — so it gets a single consolidated answer.
          Unitless rows (ratios like MV E/A) render a plain dash so the cell
          has a visible hover target and reads "unitless by design". */}
      <span className={styles.unit}>
        <RangePopover
          range={range}
          normalRange={normalRange}
          unit={unit}
          note={rangeNote}
          source={source}
          onOpenSource={onOpenSource}
          triggerClassName={styles.unitInner}
        >
          <span>{unit || '-'}</span>
          {range === 'high' && (
            <span
              className={styles.rangeArrow}
              role="img"
              aria-label="above range"
            >
              ↑
            </span>
          )}
          {range === 'low' && (
            <span
              className={styles.rangeArrow}
              role="img"
              aria-label="below range"
            >
              ↓
            </span>
          )}
          {range === null && (
            <span
              className={styles.rangeMissing}
              role="img"
              aria-label="no reference range"
            >
              ●
            </span>
          )}
        </RangePopover>
      </span>
    </div>
  );
}
